import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Sparkles, RotateCcw, Check, MessageCircle } from 'lucide-react';
import { reminderPrefs } from '@/lib/api';

const TONES = [
  { key: 'friendly', label: 'Friendly (7d)', desc: 'Gentle nudge — assumes goodwill, gives them 24-48h' },
  { key: 'firm', label: 'Firm (14d)', desc: 'Escalation without hostility — action expected now' },
  { key: 'urgent', label: 'Urgent (21d)', desc: 'Last resort — signals work may pause without payment' },
];

export default function ReminderTemplatesCard() {
  const [state, setState] = React.useState(null);
  const [draft, setDraft] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [tab, setTab] = React.useState('friendly');

  const load = React.useCallback(() => {
    reminderPrefs.templates.get().then((r) => {
      setState(r);
      const d = {};
      for (const t of ['friendly', 'firm', 'urgent']) {
        d[t] = r.templates?.[t]?.body ?? r.defaults?.[t]?.body ?? '';
      }
      setDraft(d);
    });
  }, []);

  React.useEffect(load, [load]);

  if (!state) return null;

  const isCustom = (tone) => state.templates?.[tone]?.body != null;
  const isDirty = (tone) => (draft[tone] ?? '') !== (state.templates?.[tone]?.body ?? state.defaults?.[tone]?.body ?? '');

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const patch = {};
      for (const t of ['friendly', 'firm', 'urgent']) {
        if (!isDirty(t)) continue;
        const body = draft[t] || '';
        const defaultBody = state.defaults?.[t]?.body || '';
        // If they reverted to exactly the default, send empty to remove custom.
        patch[t] = body === defaultBody ? { body: '' } : { body };
      }
      const r = await reminderPrefs.templates.save(patch);
      setState(r);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const revertToDefault = (tone) => {
    setDraft((d) => ({ ...d, [tone]: state.defaults?.[tone]?.body || '' }));
  };

  const anyDirty = ['friendly', 'firm', 'urgent'].some(isDirty);

  return (
    <Card className="border-slate-200" data-testid="reminder-templates-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#2ECC9A]" />
          <div className="text-sm font-semibold text-[#1A3A5C]">Reminder email templates</div>
          {saved && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"><Check className="mr-1 h-3 w-3"/> Saved</Badge>}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Rewrite the friendly / firm / urgent body copy in your own voice. Available placeholders:
          <span className="ml-1 font-mono">{(state.variables || []).join(' ')}</span> — hover each in the hint below.
        </p>

        <div className="mt-2 grid grid-cols-2 gap-1 text-[10px] text-slate-500 md:grid-cols-5">
          {Object.entries(state.variable_hints || {}).map(([k, v]) => (
            <div key={k} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
              <span className="font-mono text-[10px] text-[#0F766E]">{k}</span>
              <div className="text-[9px] text-slate-500">{v}</div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList data-testid="reminder-templates-tabs">
            {TONES.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`reminder-template-tab-${t.key}`}>
                <MessageCircle className="mr-1 h-3 w-3" /> {t.label}
                {isCustom(t.key) && <span className="ml-1 rounded-full bg-[#2ECC9A]/15 px-1.5 py-0.5 text-[8px] text-[#0F766E]">CUSTOM</span>}
              </TabsTrigger>
            ))}
          </TabsList>
          {TONES.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-3">
              <div className="text-[11px] text-slate-500">{t.desc}</div>
              <Textarea
                value={draft[t.key] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [t.key]: e.target.value }))}
                data-testid={`reminder-template-body-${t.key}`}
                rows={8}
                className="mt-2 font-mono text-[11px]"
              />
              <div className="mt-2 flex items-center justify-between">
                <div className="text-[10px] text-slate-500">
                  {draft[t.key]?.length ?? 0} / 4000 chars
                  {isCustom(t.key) ? <span className="ml-2 text-[#0F766E]">Currently overriding default</span> : <span className="ml-2 text-slate-400">Using default</span>}
                </div>
                <Button size="sm" variant="outline"
                        data-testid={`reminder-template-revert-${t.key}`}
                        onClick={() => revertToDefault(t.key)}
                        className="h-7 gap-1 text-[10px]">
                  <RotateCcw className="h-3 w-3" /> Reset to default
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {err && <div className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="reminder-templates-error">{err}</div>}

        <div className="mt-4">
          <Button data-testid="reminder-templates-save" onClick={save} disabled={saving || !anyDirty}
                  className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            {saving ? <Loader2 className="h-3 w-3 animate-spin"/> : 'Save templates'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
